import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { Product } from '../../core/models/product.model';
import { ProductService } from '../../core/services/product.service';
import {
  DEFAULT_SITE_CONTENT,
  FooterContent,
  HeroContent,
  ImpactStatsContent,
  JourneyContent,
  SiteContentService,
  WhyChooseUsContent,
} from '../../core/services/site-content.service';

interface NavItem {
  label: string;
  route: string;
  fragment?: string;
}

interface TrustBadge {
  icon: string;
  title: string;
  subtitle: string;
}

interface JourneyStep {
  index: string;
  title: string;
  image: string;
}

interface ComparisonGroup {
  title: string;
  tone: 'negative' | 'positive';
  items: string[];
}

interface ImpactStat {
  icon: string;
  value: number;
  suffix: string;
  label: string;
}

interface FooterLink {
  label: string;
  route?: string;
  fragment?: string;
  href?: string;
}

interface ContactItem {
  icon: string;
  label: string;
  value: string;
}

interface ProductFamilyItem {
  id: string;
  title: string;
  description: string;
}

interface CarouselProduct extends Product {
  fullDescription: string;
  backgroundTheme: string;
  background: string;
  familyItems: ProductFamilyItem[];
  icon: string;
  accent: string;
  glow: string;
  particle: string;
  stat: string;
  statLabel: string;
}

interface ProductVisualTheme {
  backgroundTheme: string;
  background: string;
  icon: string;
  accent: string;
  glow: string;
  particle: string;
}

@Component({
  selector: 'app-sr-organic-landing',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './sr-organic-landing.component.html',
  styleUrls: ['./sr-organic-landing.component.scss'],
})
export class SrOrganicLandingComponent implements AfterViewInit, OnDestroy {
  @ViewChild('impactSection') private impactSection?: ElementRef<HTMLElement>;
  @ViewChild('siteFooter') private siteFooter?: ElementRef<HTMLElement>;

  readonly heroImage = 'assets/sr-organic/mushroom-hero.png';
  readonly networkMushroomImage = 'assets/sr-organic/network-mushroom-illustration.png';
  heroContent: HeroContent = DEFAULT_SITE_CONTENT.hero;
  journeyContent: JourneyContent = DEFAULT_SITE_CONTENT.journey;
  whyChooseUsContent: WhyChooseUsContent = DEFAULT_SITE_CONTENT.whyChooseUs;
  impactStatsContent: ImpactStatsContent = DEFAULT_SITE_CONTENT.impactStats;
  footerContent: FooterContent = DEFAULT_SITE_CONTENT.footer;
  selectedProductIndex = 0;
  impactVisible = true;
  footerVisible = true;
  readonly impactCountDurationMs = 1500;
  impactDisplayValues: number[] = [];
  private impactObserver?: IntersectionObserver;
  private footerObserver?: IntersectionObserver;
  private impactAnimationFrame?: number;
  private productSubscription?: Subscription;

  readonly navItems: NavItem[] = [
    { label: 'Home', route: '/', fragment: 'home' },
    { label: 'About Us', route: '/', fragment: 'about' },
    { label: 'Products', route: '/products' },
    { label: 'Why Us', route: '/', fragment: 'why-us' },
    { label: 'Contact Us', route: '/', fragment: 'contact' },
  ];

  readonly trustBadges: TrustBadge[] = [
    {
      icon: 'leaf',
      title: '100% Natural',
      subtitle: 'Pure & Chemical Free',
    },
    {
      icon: 'sprout',
      title: 'Eco Friendly',
      subtitle: 'Sustainable Process',
    },
    {
      icon: 'flask',
      title: 'Zero Waste',
      subtitle: 'Responsible Production',
    },
    {
      icon: 'shield',
      title: 'Premium Quality',
      subtitle: 'Lab Tested',
    },
    {
      icon: 'star',
      title: 'Made In India',
      subtitle: 'Proudly Local',
    },
  ];

  journeySteps: JourneyStep[] = this.toJourneySteps(DEFAULT_SITE_CONTENT.journey.steps);

  products: CarouselProduct[] = [];
  private hasSetInitialProduct = false;
  private readonly emptyProduct: CarouselProduct = {
    id: 'empty-product',
    name: 'Products Coming Soon',
    slug: 'products-coming-soon',
    type: 'parent',
    parentId: null,
    category: 'Products',
    shortDescription: 'Product details will appear here once an active product is available.',
    longDescription: 'Product details will appear here once an active product is available.',
    fullDescription: 'Product details will appear here once an active product is available.',
    imageUrl: this.heroImage,
    backgroundTheme: 'powder',
    background: 'rgba(102, 130, 35, 0.2)',
    familyItems: [],
    status: 'active',
    displayOrder: 0,
    createdAt: '',
    updatedAt: '',
    createdOn: '',
    updatedOn: '',
    icon: '◎',
    accent: '#c8ef5a',
    glow: 'rgba(200, 239, 90, 0.44)',
    particle: 'rgba(216, 244, 99, 0.74)',
    stat: '0',
    statLabel: 'Active products',
  };

  readonly comparisonGroups: ComparisonGroup[] = [
    {
      title: 'OTHERS',
      tone: 'negative',
      items: [
        'Chemically Treated',
        'Chemical Processing',
        'Unverified Quality',
        'May Contain Additives',
        'High Waste Generation',
        'Limited Impact',
      ],
    },
    {
      title: 'S.R.ORGANIC',
      tone: 'positive',
      items: [
        '100% Natural & Organic',
        'Eco-Friendly Process',
        'Lab Tested & Consistent',
        'No Additives or Chemicals',
        'Zero Waste Approach',
        'Better for People & Planet',
      ],
    },
  ];

  impactStats: ImpactStat[] = this.toImpactStats(DEFAULT_SITE_CONTENT.impactStats.stats);

  readonly quickLinks: FooterLink[] = [
    { label: 'Home', route: '/', fragment: 'home' },
    { label: 'About Us', route: '/', fragment: 'about' },
    { label: 'Products', route: '/products' },
    { label: 'Why Us', route: '/', fragment: 'why-us' },
    { label: 'Contact Us', route: '/', fragment: 'contact' },
  ];

  readonly productLinks: FooterLink[] = [
    { label: 'Mushroom Powder', route: '/products' },
    { label: 'Mushroom Compost', route: '/products' },
    { label: 'Mushroom Extract', route: '/products' },
    { label: 'Bio Fertilizer', route: '/products' },
    { label: 'Mushroom Chunks', route: '/products' },
    { label: 'Other By-Products', route: '/products' },
  ];

  contactItems: ContactItem[] = this.toContactItems(DEFAULT_SITE_CONTENT.footer);

  readonly sponsorItems = ['GreenPlanet', 'BIOVITA', 'CecoRise', 'nutrigreen'];

  socialLinks: FooterLink[] = this.toSocialLinks(DEFAULT_SITE_CONTENT.footer);
  private allActiveProducts: Product[] = [];

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly productService: ProductService,
    private readonly siteContentService: SiteContentService,
  ) {
    this.impactDisplayValues = this.impactStats.map((stat) => stat.value);
    this.bindSiteContent();
    this.productSubscription = this.productService.getActiveProducts().subscribe((products) => {
      const selectedProductId = this.activeProduct.id;
      this.allActiveProducts = products || [];
      const parentProducts = this.allActiveProducts.filter((product) => product.type === 'parent');
      this.products = this.toCarouselProducts(parentProducts);

      if (!this.products.length) {
        this.selectedProductIndex = 0;
        this.changeDetectorRef.detectChanges();
        return;
      }

      if (!this.hasSetInitialProduct) {
        const compostIndex = this.products.findIndex((product) => product.id === 'mushroom-compost');
        this.selectedProductIndex = compostIndex >= 0 ? compostIndex : 0;
        this.hasSetInitialProduct = true;
        this.changeDetectorRef.detectChanges();
        return;
      }

      const nextSelectedIndex = this.products.findIndex((product) => product.id === selectedProductId);
      this.selectedProductIndex =
        nextSelectedIndex >= 0 ? nextSelectedIndex : Math.min(this.selectedProductIndex, this.products.length - 1);
      this.changeDetectorRef.detectChanges();
    });
  }

  get activeProduct(): CarouselProduct {
    return this.products[this.selectedProductIndex] ?? this.products[0] ?? this.emptyProduct;
  }

  get selectedProduct(): CarouselProduct {
    return this.activeProduct;
  }

  getChildrenForSelectedProduct(): Product[] {
    if (!this.selectedProduct?.id) {
      return [];
    }

    return this.allActiveProducts
      .filter((product) =>
        product.type === 'child' &&
        product.parentId === this.selectedProduct.id &&
        product.status === 'active'
      )
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || a.name.localeCompare(b.name));
  }

  ngAfterViewInit(): void {
    const section = this.impactSection?.nativeElement;

    if (!section || typeof IntersectionObserver === 'undefined') {
      this.revealImpactSection();
      return;
    }

    this.impactObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        this.revealImpactSection();
        this.impactObserver?.disconnect();
      },
      { threshold: 0.28 },
    );

    this.impactObserver.observe(section);

    const footer = this.siteFooter?.nativeElement;

    if (!footer || typeof IntersectionObserver === 'undefined') {
      this.footerVisible = true;
      return;
    }

    this.footerObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        this.footerVisible = true;
        this.footerObserver?.disconnect();
      },
      { threshold: 0.18 },
    );

    this.footerObserver.observe(footer);
  }

  ngOnDestroy(): void {
    this.impactObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.productSubscription?.unsubscribe();
    this.siteContentSubscription.unsubscribe();

    if (this.impactAnimationFrame) {
      cancelAnimationFrame(this.impactAnimationFrame);
    }
  }

  selectProduct(index: number): void {
    if (!this.products[index]) {
      return;
    }

    this.selectedProductIndex = index;
  }

  showPreviousProduct(): void {
    if (!this.products.length) {
      return;
    }

    this.selectedProductIndex =
      (this.selectedProductIndex - 1 + this.products.length) % this.products.length;
  }

  showNextProduct(): void {
    if (!this.products.length) {
      return;
    }

    this.selectedProductIndex = (this.selectedProductIndex + 1) % this.products.length;
  }

  getProductPositionClass(index: number): string {
    const productCount = this.products.length;

    if (!productCount) {
      return '';
    }

    if (productCount === 1) {
      return 'is-active';
    }

    const offset = (index - this.selectedProductIndex + productCount) % productCount;

    if (offset === 0) {
      return 'is-active';
    }

    if (offset === productCount - 1) {
      return 'is-left';
    }

    if (offset === 1) {
      return 'is-right';
    }

    if (offset === productCount - 2) {
      return 'is-far-left';
    }

    if (offset === 2) {
      return 'is-far-right';
    }

    return 'is-far-left';
  }

  trackByTitle(_: number, item: string | NavItem | TrustBadge | JourneyStep | Product | ProductFamilyItem): string {
    if (typeof item === 'string') {
      return item;
    }

    if ('label' in item) {
      return item.label;
    }

    return 'name' in item ? item.name : item.title;
  }

  private toCarouselProducts(products: Product[]): CarouselProduct[] {
    const activeParents = products
      .filter((product) => product.type === 'parent' && product.status === 'active')
      .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));

    return activeParents.map((product) => {
      const theme = this.getProductTheme(product);

      return {
        ...product,
        fullDescription: product.longDescription,
        backgroundTheme: theme.backgroundTheme,
        background: theme.background,
        familyItems: [],
        icon: theme.icon,
        accent: theme.accent,
        glow: theme.glow,
        particle: theme.particle,
        stat: String(this.getChildProductsForParent(product.id).length),
        statLabel: 'Child products',
      };
    });
  }

  private getChildProductsForParent(parentId: string): Product[] {
    return this.allActiveProducts
      .filter((product) => product.type === 'child' && product.parentId === parentId && product.status === 'active')
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || a.name.localeCompare(b.name));
  }

  private readonly siteContentSubscription = new Subscription();

  private bindSiteContent(): void {
    this.siteContentSubscription.add(
      this.siteContentService.getSection<HeroContent>('hero').subscribe((content) => {
        this.heroContent = { ...DEFAULT_SITE_CONTENT.hero, ...(content || {}) };
        this.changeDetectorRef.detectChanges();
      }),
    );

    this.siteContentSubscription.add(
      this.siteContentService.getSection<JourneyContent>('journey').subscribe((content) => {
        this.journeyContent = { ...DEFAULT_SITE_CONTENT.journey, ...(content || {}) };
        this.journeySteps = this.toJourneySteps(this.journeyContent.steps);
        this.changeDetectorRef.detectChanges();
      }),
    );

    this.siteContentSubscription.add(
      this.siteContentService.getSection<WhyChooseUsContent>('whyChooseUs').subscribe((content) => {
        this.whyChooseUsContent = { ...DEFAULT_SITE_CONTENT.whyChooseUs, ...(content || {}) };
        this.changeDetectorRef.detectChanges();
      }),
    );

    this.siteContentSubscription.add(
      this.siteContentService.getSection<ImpactStatsContent>('impactStats').subscribe((content) => {
        this.impactStatsContent = { ...DEFAULT_SITE_CONTENT.impactStats, ...(content || {}) };
        this.impactStats = this.toImpactStats(this.impactStatsContent.stats);
        this.impactDisplayValues = this.impactStats.map((stat) => stat.value);
        this.changeDetectorRef.detectChanges();
      }),
    );

    this.siteContentSubscription.add(
      this.siteContentService.getSection<FooterContent>('footer').subscribe((content) => {
        this.footerContent = { ...DEFAULT_SITE_CONTENT.footer, ...(content || {}) };
        this.contactItems = this.toContactItems(this.footerContent);
        this.socialLinks = this.toSocialLinks(this.footerContent);
        this.changeDetectorRef.detectChanges();
      }),
    );
  }

  private toJourneySteps(steps: string[] = DEFAULT_SITE_CONTENT.journey.steps): JourneyStep[] {
    return Array.from({ length: 6 }, (_, index) => ({
      index: String(index + 1).padStart(2, '0'),
      title: steps[index] || DEFAULT_SITE_CONTENT.journey.steps[index] || '',
      image: 'assets/sr-organic/mushroom-hero.png',
    }));
  }

  private toImpactStats(stats = DEFAULT_SITE_CONTENT.impactStats.stats): ImpactStat[] {
    const icons = ['♻', '♧', 'CO2', '⚡'];

    return Array.from({ length: 4 }, (_, index) => {
      const stat = stats[index] || DEFAULT_SITE_CONTENT.impactStats.stats[index];
      const parsed = this.parseDisplayNumber(stat.value);

      return {
        icon: icons[index] || '•',
        value: parsed.value,
        suffix: parsed.suffix,
        label: stat.label,
      };
    });
  }

  private parseDisplayNumber(value: string): { value: number; suffix: string } {
    const text = String(value || '').trim();
    const numericValue = Number(text.replace(/[^0-9.]/g, '')) || 0;
    const suffix = text.includes('%') ? '%' : text.includes('+') ? '+' : '';

    return { value: numericValue, suffix };
  }

  private toContactItems(footer: FooterContent): ContactItem[] {
    return [
      { icon: '☎', label: 'Phone', value: footer.phone || DEFAULT_SITE_CONTENT.footer.phone },
      { icon: '✉', label: 'Email', value: footer.email || DEFAULT_SITE_CONTENT.footer.email },
      { icon: '⌖', label: 'Address', value: footer.address || DEFAULT_SITE_CONTENT.footer.address },
    ];
  }

  private toSocialLinks(footer: FooterContent): FooterLink[] {
    return [
      { label: 'Facebook', href: footer.facebook || '#' },
      { label: 'Instagram', href: footer.instagram || '#' },
      { label: 'LinkedIn', href: footer.linkedin || '#' },
      { label: 'YouTube', href: footer.youtube || '#' },
    ];
  }

  private getProductTheme(product: Product): ProductVisualTheme {
    const customTheme = this.getCustomProductTheme(product.themeColor);

    if (customTheme) {
      return customTheme;
    }

    const key = product.id.includes('compost')
      ? 'compost'
      : product.id.includes('extract')
        ? 'extract'
        : product.id.includes('fertilizer')
          ? 'fertilizer'
          : product.id.includes('grow') || product.id.includes('other')
            ? 'fresh'
            : 'powder';

    const themes: Record<string, ProductVisualTheme> = {
      powder: {
        backgroundTheme: 'powder',
        background: 'rgba(102, 130, 35, 0.2)',
        icon: '◎',
        accent: '#c8ef5a',
        glow: 'rgba(200, 239, 90, 0.44)',
        particle: 'rgba(216, 244, 99, 0.74)',
      },
      compost: {
        backgroundTheme: 'compost',
        background: 'rgba(119, 82, 38, 0.24)',
        icon: '✦',
        accent: '#d7a85f',
        glow: 'rgba(215, 168, 95, 0.44)',
        particle: 'rgba(229, 178, 96, 0.72)',
      },
      extract: {
        backgroundTheme: 'extract',
        background: 'rgba(139, 111, 44, 0.22)',
        icon: '◌',
        accent: '#f2d678',
        glow: 'rgba(242, 214, 120, 0.45)',
        particle: 'rgba(255, 224, 128, 0.76)',
      },
      fertilizer: {
        backgroundTheme: 'fertilizer',
        background: 'rgba(37, 118, 78, 0.22)',
        icon: '✺',
        accent: '#73dda3',
        glow: 'rgba(115, 221, 163, 0.42)',
        particle: 'rgba(131, 240, 178, 0.72)',
      },
      fresh: {
        backgroundTheme: 'fresh',
        background: 'rgba(102, 130, 35, 0.2)',
        icon: '⬡',
        accent: '#c8ef5a',
        glow: 'rgba(200, 239, 90, 0.44)',
        particle: 'rgba(216, 244, 99, 0.74)',
      },
    };

    return themes[key];
  }

  private getCustomProductTheme(themeColor?: string): ProductVisualTheme | null {
    const color = themeColor?.trim();

    if (!color || !/^#[0-9a-f]{6}$/i.test(color)) {
      return null;
    }

    const rgb = this.hexToRgb(color);

    if (!rgb) {
      return null;
    }

    return {
      backgroundTheme: 'custom',
      background: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.24)`,
      icon: '◎',
      accent: color,
      glow: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.44)`,
      particle: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.74)`,
    };
  }

  private hexToRgb(hexColor: string): { r: number; g: number; b: number } | null {
    const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hexColor);

    if (!match) {
      return null;
    }

    return {
      r: Number.parseInt(match[1], 16),
      g: Number.parseInt(match[2], 16),
      b: Number.parseInt(match[3], 16),
    };
  }

  trackByComparisonTitle(_: number, item: ComparisonGroup): string {
    return item.title;
  }

  trackByLabel(_: number, item: ImpactStat): string {
    return item.label;
  }

  trackByFooterLabel(_: number, item: FooterLink | ContactItem): string {
    return item.label;
  }

  trackBySponsor(_: number, item: string): string {
    return item;
  }

  formatImpactValue(value: number): string {
    return Math.round(value).toLocaleString('en-IN');
  }

  private revealImpactSection(): void {
    if (this.impactVisible) {
      return;
    }

    this.impactVisible = true;
    this.animateImpactStats();
  }

  private animateImpactStats(): void {
    const startedAt = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - startedAt) / this.impactCountDurationMs, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      this.impactDisplayValues = this.impactStats.map((stat) => stat.value * easedProgress);

      if (progress < 1) {
        this.impactAnimationFrame = requestAnimationFrame(tick);
      }
    };

    this.impactAnimationFrame = requestAnimationFrame(tick);
  }
}
